import { useRef, useState, useCallback, useEffect } from 'react';

/**
 * useCamera — manages webcam stream + single-frame capture
 * Returns: { videoRef, camReady, camError, startCam, stopCam, captureFrame }
 */
export function useCamera() {
  const videoRef  = useRef(null);
  const streamRef = useRef(null);
  const [camReady, setCamReady]  = useState(false);
  const [camError, setCamError]  = useState(null);

  const startCam = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setCamReady(true);
      setCamError(null);
    } catch (err) {
      setCamError('Kamera tidak bisa diakses. Cek izin browser kamu.');
    }
  }, []);

  const stopCam = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setCamReady(false);
  }, []);

  /** Captures current frame from videoRef. Applies CSS filter string via canvas. */
  const captureFrame = useCallback((filterCss = 'none') => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return null;
    const c = document.createElement('canvas');
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    const ctx = c.getContext('2d');
    // Mirror horizontally (selfie-style)
    ctx.save();
    ctx.scale(-1, 1);
    ctx.drawImage(v, -c.width, 0, c.width, c.height);
    ctx.restore();
    return c.toDataURL('image/jpeg', 0.92);
  }, []);

  // Auto-start on mount, cleanup on unmount
  useEffect(() => {
    startCam();
    return () => stopCam();
  }, [startCam, stopCam]);

  return { videoRef, streamRef, camReady, camError, startCam, stopCam, captureFrame };
}

/**
 * useCountdown — runs a countdown from N, calls onDone when it reaches 0
 */
export function useCountdown() {
  const [value, setValue]   = useState(null);
  const timerRef = useRef(null);

  const start = useCallback((from, onDone) => {
    setValue(from);
    let current = from;
    timerRef.current = setInterval(() => {
      current--;
      if (current > 0) {
        setValue(current);
      } else {
        clearInterval(timerRef.current);
        setValue(null);
        onDone();
      }
    }, 1000);
  }, []);

  const cancel = useCallback(() => {
    clearInterval(timerRef.current);
    setValue(null);
  }, []);

  useEffect(() => () => clearInterval(timerRef.current), []);

  return { countdown: value, startCountdown: start, cancelCountdown: cancel };
}

/**
 * useVideoRecorder — MediaRecorder wrapper
 */
export function useVideoRecorder(streamRef) {
  const recRef   = useRef(null);
  const chunks   = useRef([]);
  const timerRef = useRef(null);

  const [recording,  setRecording]  = useState(false);
  const [videoBlob,  setVideoBlob]  = useState(null);
  const [elapsed,    setElapsed]    = useState(0);

  const start = useCallback((maxSeconds = 30) => {
    if (!streamRef.current) return;
    chunks.current = [];
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
      ? 'video/webm;codecs=vp9,opus'
      : 'video/webm';
    const mr = new MediaRecorder(streamRef.current, { mimeType });
    mr.ondataavailable = e => { if (e.data.size > 0) chunks.current.push(e.data); };
    mr.onstop = () => setVideoBlob(new Blob(chunks.current, { type: 'video/webm' }));
    mr.start();
    recRef.current = mr;
    setRecording(true);
    setVideoBlob(null);
    setElapsed(0);
    let sec = 0;
    timerRef.current = setInterval(() => {
      sec++;
      setElapsed(sec);
      if (sec >= maxSeconds) stop();
    }, 1000);
  }, [streamRef]); // eslint-disable-line

  const stop = useCallback(() => {
    recRef.current?.stop();
    clearInterval(timerRef.current);
    setRecording(false);
  }, []);

  const download = useCallback(() => {
    if (!videoBlob) return;
    const url = URL.createObjectURL(videoBlob);
    const a = document.createElement('a');
    a.href = url; a.download = 'HBD-Nof-Video.webm'; a.click();
    URL.revokeObjectURL(url);
  }, [videoBlob]);

  useEffect(() => () => clearInterval(timerRef.current), []);

  return { recording, videoBlob, elapsed, startRecording: start, stopRecording: stop, downloadVideo: download };
}
